import { Deferred, Head, router, usePage } from '@inertiajs/react'
import { GitHubLogoIcon } from '@radix-ui/react-icons'
import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import { ExternalLink, GitMerge, TrendingDown, TrendingUp, User } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { RelativeTime } from '@/components/changelog/relative-time'
import { DashboardTabs } from '@/components/dashboard/dashboard-tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import { Skeleton } from '@/components/ui/skeleton'
import { DashboardShell } from '@/layouts/dashboard-shell'
import { organizationAnalyticsPath } from '@/routes'
import type { AnalyticsSnapshot, OrganizationsAnalyticsShow } from '@/types'

const chartConfig = {
  commits: { label: 'Changes', color: 'hsl(var(--chart-1))' },
} satisfies ChartConfig

const pieChartConfig = {
  feature: { label: 'Features', color: 'hsl(var(--chart-1))' },
  bugfix: { label: 'Bug Fixes', color: 'hsl(var(--chart-2))' },
  improvement: { label: 'Improvements', color: 'hsl(var(--chart-3))' },
  chore: { label: 'Chore', color: 'hsl(var(--chart-4))' },
  refactor: { label: 'Refactor', color: 'hsl(var(--chart-5))' },
} satisfies ChartConfig

function formatTrend(value: number) {
  return Math.abs(value).toLocaleString(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  })
}

function TrendFooter({ label, trend, dayCount }: { label: string; trend: number; dayCount: number }) {
  const positive = trend >= 0
  const TrendIcon = positive ? TrendingUp : TrendingDown
  const trendColor = positive ? 'text-green-700' : 'text-red-600'

  return (
    <CardFooter className="flex-col gap-2 text-sm">
      <div className="flex items-center gap-2 font-medium">
        <span className="leading-none">
          Trending {positive ? 'up' : 'down'} by <span className={trendColor}>{formatTrend(trend)}%</span> {label}
        </span>
        <TrendIcon className={`size-4 ${trendColor}`} />
      </div>
      <div className="text-muted-foreground leading-none">{dayCount} days of activity data</div>
    </CardFooter>
  )
}

function AnalyticsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Card key={index}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="mb-2 h-8 w-16" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <Skeleton className="mx-auto h-6 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    </div>
  )
}

function AnalyticsContent({
  analytics,
  from,
  to,
}: {
  analytics: AnalyticsSnapshot
  from: string
  to: string
}) {
  const dayCount = differenceInCalendarDays(parseISO(to), parseISO(from)) + 1
  const pieData = analytics.tagDistribution.chartData.filter((row) => row.value > 0)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Shipments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.kpis.totalShipments.toLocaleString()}</div>
            <p className="text-muted-foreground text-xs">In selected date range</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">PR Time to Merge</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics.kpis.prTimeToMergeCount > 0 && analytics.kpis.prTimeToMergeHours != null ? (
                analytics.kpis.prTimeToMergeHours < 24 ? (
                  `${analytics.kpis.prTimeToMergeHours.toFixed(1)} hours`
                ) : (
                  `${(analytics.kpis.prTimeToMergeHours / 24).toFixed(1)} days`
                )
              ) : (
                'No data'
              )}
            </div>
            <p className="text-muted-foreground text-xs">
              Average time from PR open to merge ({analytics.kpis.prTimeToMergeCount} PRs)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Lines Added</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">+{analytics.kpis.linesAdded.toLocaleString()}</div>
            <p className="text-muted-foreground text-xs">In selected date range</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Lines Removed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">-{analytics.kpis.linesRemoved.toLocaleString()}</div>
            <p className="text-muted-foreground text-xs">In selected date range</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="items-center">
          <CardTitle>Team Leaderboard</CardTitle>
          <CardDescription>
            Ranked by total lines changed from {format(parseISO(from), 'MMM d')}
            {from !== to ? ` to ${format(parseISO(to), 'MMM d')}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {analytics.leaderboard.length === 0 ? (
            <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">
              No data for the selected filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b text-left">
                    <th className="py-2 pr-4 font-medium">Author</th>
                    <th className="py-2 pr-4 text-right font-medium">Code Changes</th>
                    <th className="py-2 pr-4 text-right font-medium">Lines Added</th>
                    <th className="py-2 pr-4 text-right font-medium">Lines Removed</th>
                    <th className="py-2 text-right font-medium">Total Lines Changed</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.leaderboard.map((row) => (
                    <tr key={row.author} className="border-b last:border-0">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            {row.authorAvatarUrl ? <AvatarImage src={row.authorAvatarUrl} alt={row.author} /> : null}
                            <AvatarFallback>{row.author.charAt(0).toUpperCase()}</AvatarFallback>
                          </Avatar>
                          <span className="max-w-40 truncate">{row.author}</span>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-right font-mono">{row.codeChanges.toLocaleString()}</td>
                      <td className="py-3 pr-4 text-right font-semibold text-green-700">
                        +{row.linesAdded.toLocaleString()}
                      </td>
                      <td className="py-3 pr-4 text-right font-semibold text-red-700">
                        -{row.linesRemoved.toLocaleString()}
                      </td>
                      <td className="py-3 text-right font-mono">{row.totalLinesChanged.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Most Recent Code Changes</CardTitle>
            <CardDescription>Latest 10 shipments for selected filters</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-[320px] overflow-y-auto">
              {analytics.recentCommits.length === 0 ? (
                <div className="text-muted-foreground flex h-32 items-center justify-center text-sm">
                  No data available for the selected filters
                </div>
              ) : (
                <div className="flex flex-col">
                  {analytics.recentCommits.map((commit) => (
                    <a
                      key={commit.sha}
                      href={commit.htmlUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="group hover:bg-muted flex items-start gap-2 border-b px-2 py-2 text-sm transition-colors last:border-b-0"
                    >
                      <GitMerge className="size-4 shrink-0 text-purple-500" />
                      <div className="min-w-0 flex-1">
                        <div>
                          <span className="text-muted-foreground mr-2 font-semibold">{commit.repositoryName}</span>
                          <span className="font-semibold break-words">{commit.summary}</span>
                        </div>
                        <span className="text-muted-foreground text-xs">
                          {commit.pullRequestNumber && commit.branch === 'main'
                            ? `#${commit.pullRequestNumber}`
                            : `commit ${commit.sha.slice(0, 7)}`}{' '}
                          by {commit.author}{' '}
                          {commit.branch && commit.branch !== 'main' ? `to ${commit.branch} ` : 'was merged '}
                          <RelativeTime timestamp={commit.isoDate} />
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-5 w-5 shrink-0">
                          {commit.authorAvatarUrl ? (
                            <AvatarImage src={commit.authorAvatarUrl} alt={commit.author} />
                          ) : null}
                          <AvatarFallback className="text-xs">{commit.author.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <ExternalLink
                          className="text-muted-foreground size-3.5 shrink-0 opacity-70 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 motion-reduce:transition-none"
                          aria-hidden="true"
                        />
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="flex flex-col">
          <CardHeader className="items-center pb-0">
            <CardTitle>Code Changes Distribution</CardTitle>
            <CardDescription>
              Analysis of commit types for {format(parseISO(from), 'MMM dd')} - {format(parseISO(to), 'MMM dd')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 pb-0">
            {pieData.length === 0 ? (
              <div className="text-muted-foreground flex h-[220px] items-center justify-center text-sm">
                No data available for the selected filters
              </div>
            ) : (
              <div className="h-[220px] w-full sm:h-[250px]">
                <ChartContainer config={pieChartConfig} className="mx-auto h-full w-full">
                  <PieChart>
                    <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius="35%"
                      outerRadius="65%"
                      strokeWidth={3}
                      paddingAngle={1}
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.key} fill={entry.color} />
                      ))}
                      <Label
                        content={({ viewBox }) => {
                          if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                            return (
                              <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) - 8}
                                  className="fill-foreground text-xl font-bold"
                                >
                                  {analytics.tagDistribution.total.toLocaleString()}
                                </tspan>
                                <tspan
                                  x={viewBox.cx}
                                  y={(viewBox.cy || 0) + 12}
                                  className="fill-muted-foreground text-xs"
                                >
                                  Changes
                                </tspan>
                              </text>
                            )
                          }
                          return null
                        }}
                      />
                    </Pie>
                  </PieChart>
                </ChartContainer>
              </div>
            )}
          </CardContent>
          <TrendFooter label="in this period" trend={analytics.trends.tagDistribution} dayCount={dayCount} />
        </Card>
      </div>

      <Card className="flex flex-col">
        <CardHeader className="items-center">
          <CardTitle>Activity Metrics</CardTitle>
          <CardDescription>
            Activity details for {format(parseISO(from), 'MMM dd')} - {format(parseISO(to), 'MMM dd')}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 pb-0">
          <div className="h-[250px] w-full sm:h-[300px]">
            <ChartContainer config={chartConfig} className="h-full w-full">
              <BarChart
                accessibilityLayer
                data={analytics.dailyBuckets}
                margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="2 3" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 9 }}
                  interval={analytics.dailyBuckets.length > 7 ? 'equidistantPreserveStart' : 0}
                  height={30}
                />
                <YAxis
                  tick={{ fontSize: 9 }}
                  width={25}
                  tickCount={4}
                  domain={[0, (dataMax: number) => Math.max(1, Math.ceil(dataMax * 1.2))]}
                />
                <Tooltip />
                <Legend
                  wrapperStyle={{ fontSize: '9px' }}
                  iconSize={8}
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                />
                <Bar dataKey="commits" fill="#E6A91A" name="Changes" barSize={8} />
                <Bar dataKey="linesAdded" fill="#15803d" name="Lines Added" barSize={8} />
                <Bar dataKey="linesRemoved" fill="#dc2626" name="Lines Removed" barSize={8} />
              </BarChart>
            </ChartContainer>
          </div>
        </CardContent>
        <TrendFooter label="in this period" trend={analytics.trends.activity} dayCount={dayCount} />
      </Card>
    </div>
  )
}

export default function OrganizationAnalyticsShow({
  organization,
  repositories,
  from,
  to,
  repositoryIds,
  authors,
  availableAuthors,
  analytics,
}: OrganizationsAnalyticsShow) {
  const { analyticsEnabled } = usePage().props
  const orgId = organization.id

  const applyFilters = (next: {
    from?: string
    to?: string
    repositoryIds?: string[]
    authors?: string[]
  }) => {
    router.get(
      organizationAnalyticsPath(orgId, {
        from: next.from ?? from,
        to: next.to ?? to,
        repository_ids: next.repositoryIds ?? repositoryIds,
        authors: next.authors ?? authors,
      }),
      {},
      { preserveState: true, preserveScroll: true },
    )
  }

  return (
    <>
      <Head title={`${organization.name} — Analytics`} />
      <DashboardShell organization={organization} repositories={repositories} hideTitle>
        <DashboardTabs organizationId={orgId} active="analytics" analyticsEnabled={analyticsEnabled} />

        <div className="space-y-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl leading-none font-semibold tracking-tight mb-8">Analytics</h1>
              <p className="text-muted-foreground text-sm">
                View code change statistics across your repositories
              </p>
            </div>

            <div className="flex w-full flex-col flex-wrap gap-2 sm:w-auto sm:flex-row">
              <DateRangePicker
                from={parseISO(from)}
                to={parseISO(to)}
                onChange={({ from: nextFrom, to: nextTo }) =>
                  applyFilters({
                    from: format(nextFrom, 'yyyy-MM-dd'),
                    to: format(nextTo, 'yyyy-MM-dd'),
                  })
                }
              />
              <MultiSelectFilter
                label="Repositories"
                icon={<GitHubLogoIcon className="mr-2 size-4" />}
                options={repositories.map((repo) => ({ value: repo.id, label: repo.fullName }))}
                selected={repositoryIds}
                onChange={(next) => applyFilters({ repositoryIds: next })}
                searchPlaceholder="Search repositories…"
                emptyText="No repositories found."
              />
              <MultiSelectFilter
                label="Authors"
                icon={<User className="mr-2 size-4" />}
                options={availableAuthors.map((author) => ({ value: author, label: author }))}
                selected={authors}
                onChange={(next) => applyFilters({ authors: next })}
                searchPlaceholder="Search authors…"
                emptyText="No authors found."
              />
            </div>
          </div>

          <Deferred
            data="analytics"
            fallback={<AnalyticsSkeleton />}
            rescue={({ reloading }) => (
              <Card>
                <CardContent className="flex flex-col items-center justify-center gap-4 py-10">
                  <div className="text-center">
                    <h3 className="text-lg">Failed to load analytics data</h3>
                    <p className="text-muted-foreground">There was an error loading your code change data.</p>
                  </div>
                  <Button type="button" disabled={reloading} onClick={() => router.reload({ only: ['analytics'] })}>
                    Retry
                  </Button>
                </CardContent>
              </Card>
            )}
          >
            {analytics ? <AnalyticsContent analytics={analytics} from={from} to={to} /> : null}
          </Deferred>
        </div>
      </DashboardShell>
    </>
  )
}
